(function () {
  const PREVIEW_SIZE_MULTIPLIER = 2.6;
  const PREVIEW_MIN_RENDER_SIZE = 32;
  const PREVIEW_MAX_RENDER_SIZE = 420;
  const FEATURE_LABELS = {
    aalt: 'All alternates',
    calt: 'Contextual alternates',
    case: 'Case-sensitive',
    dlig: 'Discretionary ligatures',
    frac: 'Fractions',
    hist: 'Historical',
    liga: 'Ligatures',
    lnum: 'Lining figures',
    locl: 'Localized forms',
    onum: 'Oldstyle figures',
    ordn: 'Ordinals',
    pnum: 'Proportional figures',
    salt: 'Stylistic alternates',
    sinf: 'Scientific inferiors',
    smcp: 'Small caps',
    ss01: 'SS01',
    ss02: 'SS02',
    ss03: 'SS03',
    ss04: 'SS04',
    ss05: 'SS05',
    ss06: 'SS06',
    ss07: 'SS07',
    ss08: 'SS08',
    ss09: 'SS09',
    ss10: 'SS10',
    subs: 'Subscript',
    sups: 'Superscript',
    swsh: 'Swash',
    titl: 'Titling',
    tnum: 'Tabular figures'
  };
  const FEATURE_EXCLUDE = new Set(['ccmp', 'kern', 'locl', 'mark', 'mkmk']);
  const fontFeatureCache = new Map();

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function normalizeFeatureKey(value) {
    return String(value || '')
      .replace(/-\d+$/, '')
      .replace(/trial/gi, '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^serial-f/, 'serialf')
      .replace(/^-+|-+$/g, '');
  }

  function getFeatureMap() {
    return window.DUMDUM_TYPE_FEATURES || {};
  }

  function getFeaturesForKey(key) {
    if (!key) return [];
    const featureMap = getFeatureMap();
    const normalizedKey = normalizeFeatureKey(key);
    const directFeatures = featureMap[normalizedKey];
    if (Array.isArray(directFeatures) && directFeatures.length > 0) {
      return directFeatures;
    }

    const familyKey = normalizedKey.replace(/-(regular|thin|light|book|medium|semibold|bold|black|heavy|xl|xxl|xxxl|xs)$/i, '');
    const familyFeatures = featureMap[familyKey];
    if (Array.isArray(familyFeatures) && familyFeatures.length > 0) {
      return familyFeatures;
    }

    const prefixMatch = Object.keys(featureMap).find((mapKey) => normalizedKey.startsWith(mapKey) || mapKey.startsWith(familyKey));
    if (prefixMatch && Array.isArray(featureMap[prefixMatch])) {
      return featureMap[prefixMatch];
    }

    return [];
  }

  function extractFeaturesFromTables(font) {
    if (!font || !font.tables) return [];

    const tags = new Set();

    ['gsub', 'gpos'].forEach((tableName) => {
      const table = font.tables[tableName];
      const features = table && Array.isArray(table.features) ? table.features : [];
      features.forEach((feature) => {
        const tag = feature && feature.tag ? String(feature.tag).trim() : '';
        if (tag) tags.add(tag);
      });
    });

    return Array.from(tags)
      .filter((tag) => !FEATURE_EXCLUDE.has(tag))
      .filter((tag) => FEATURE_LABELS[tag] || /^ss\d{2}$/i.test(tag))
      .sort((left, right) => left.localeCompare(right, undefined, { numeric: true }));
  }

  function isWoff2(buffer) {
    if (!buffer || buffer.byteLength < 4) return false;
    const signature = new Uint8Array(buffer, 0, 4);
    return signature[0] === 0x77 && signature[1] === 0x4f && signature[2] === 0x46 && signature[3] === 0x32;
  }

  async function parseFontBuffer(buffer) {
    if (!window.opentype) {
      throw new Error('OpenType parser is unavailable');
    }

    if (isWoff2(buffer)) {
      if (!window.woffLib || typeof window.woffLib.woff2Decode !== 'function') {
        throw new Error('WOFF2 decoder is unavailable');
      }

      const decoded = await window.woffLib.woff2Decode(new Uint8Array(buffer));
      const decodedBuffer = decoded.buffer.slice(decoded.byteOffset, decoded.byteOffset + decoded.byteLength);
      return window.opentype.parse(decodedBuffer);
    }

    return window.opentype.parse(buffer);
  }

  async function loadFeaturesFromFontUrl(fontUrl) {
    if (!fontUrl || !window.opentype) return { features: [], source: 'missing-opentype' };
    if (fontFeatureCache.has(fontUrl)) return fontFeatureCache.get(fontUrl);

    const featurePromise = fetch(fontUrl, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Unable to load font: ${response.status}`);
        }
        return response.arrayBuffer();
      })
      .then(async (buffer) => {
        const font = await parseFontBuffer(buffer);
        return { features: extractFeaturesFromTables(font), source: 'opentype' };
      })
      .catch((error) => ({ features: [], source: 'error', error: String(error && error.message ? error.message : error) }));

    fontFeatureCache.set(fontUrl, featurePromise);
    return featurePromise;
  }

  function getFeatureLabel(feature) {
    if (feature && typeof feature === 'object') {
      return feature.label || FEATURE_LABELS[feature.tag] || String(feature.tag || '').toUpperCase();
    }
    return FEATURE_LABELS[feature] || String(feature || '').toUpperCase();
  }

  function getFeatureTag(feature) {
    return typeof feature === 'object' ? feature.tag : feature;
  }

  function getSelectedFeatureState(item, featureKey) {
    if (!item.__featureState) item.__featureState = {};
    if (!item.__featureState[featureKey]) item.__featureState[featureKey] = [];
    return item.__featureState[featureKey];
  }

  function updateFeatureDebug() {}

  function updateTextTransform(item, transformValue) {
    const input = item.querySelector('.custom-input');
    if (!input) return;

    const nextValue = ['uppercase', 'lowercase'].includes(transformValue) ? transformValue : 'none';
    input.style.setProperty('text-transform', nextValue, 'important');

    item.querySelectorAll('.type-archive-transform-pill[data-transform-value]').forEach((pill) => {
      pill.classList.toggle('is-active', pill.dataset.transformValue === nextValue);
    });
  }

  function applyFeatureSettings(item) {
    const input = item.querySelector('.custom-input');
    if (!input) return;

    const featureKey = item.dataset.activeFeatureKey;
    const activeTags = featureKey ? getSelectedFeatureState(item, featureKey) : [];

    if (!activeTags || activeTags.length === 0) {
      input.style.removeProperty('font-feature-settings');
      return;
    }

    const featureString = activeTags.map((tag) => `"${tag}" 1`).join(', ');
    input.style.setProperty('font-feature-settings', featureString, 'important');
  }

  function renderFeatureControls(item, featureKey, featuresOverride) {
    const wrapper = item.querySelector('.type-archive-features');
    const list = item.querySelector('.type-archive-features__list');

    if (!wrapper || !list) return;

    const features = (Array.isArray(featuresOverride) && featuresOverride.length ? featuresOverride : getFeaturesForKey(featureKey))
      .map((feature) => ({ tag: getFeatureTag(feature), label: getFeatureLabel(feature) }))
      .filter((feature) => feature.tag);

    item.dataset.activeFeatureKey = featureKey ? normalizeFeatureKey(featureKey) : '';

    if (features.length === 0) {
      wrapper.hidden = true;
      list.innerHTML = '';
      applyFeatureSettings(item);
      return;
    }

    const activeTags = getSelectedFeatureState(item, item.dataset.activeFeatureKey);

    list.innerHTML = features
      .map((feature) => {
        const checked = activeTags.includes(feature.tag) ? ' checked' : '';
        return `
          <label class="type-archive-feature">
            <input type="checkbox" class="type-archive-feature__input" data-feature-tag="${feature.tag}"${checked}>
            <span class="type-archive-feature__box"></span>
            <span class="type-archive-feature__label">${feature.label}</span>
          </label>
        `;
      })
      .join('');

    wrapper.hidden = false;
    applyFeatureSettings(item);
  }

  function syncFeatureState(item) {
    const featureKey = item.dataset.activeFeatureKey;
    if (!featureKey) return;

    const checkedTags = Array.from(item.querySelectorAll('.type-archive-feature__input:checked'))
      .map((input) => input.dataset.featureTag)
      .filter(Boolean);

    if (!item.__featureState) item.__featureState = {};
    item.__featureState[featureKey] = checkedTags;
    applyFeatureSettings(item);

    const slider = item.querySelector('.font-size-slider');
    if (slider) {
      updateSliderPreview(item, slider);
    }
  }

  function getBasePreviewSize(slider) {
    const value = Number(slider.value || slider.min || 0);
    const viewportBoost = window.innerWidth >= 1200 ? 1.04 : 1;
    const size = value * PREVIEW_SIZE_MULTIPLIER * viewportBoost;
    return clamp(size, PREVIEW_MIN_RENDER_SIZE, PREVIEW_MAX_RENDER_SIZE);
  }

  function fitPreviewToContainer(input, slider) {
    if (!input || !slider) return;
    const baseSize = getBasePreviewSize(slider);
    input.style.setProperty('font-size', `${baseSize}px`, 'important');
  }

  function moneyFromCents(cents) {
    if (typeof cents !== 'number') return '';
    return `$ ${(cents / 100).toFixed(2)}`;
  }

  function parseVariants(item) {
    const node = item.querySelector('.js-variant-data');
    if (!node) return [];
    try {
      const parsed = JSON.parse(node.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (_err) {
      return [];
    }
  }

  function getOptionPosition(select) {
    return Number(select.dataset.optionPosition || 0);
  }

  function matchVariantBySelections(variants, selects) {
    return variants.find((variant) =>
      selects.every((select) => {
        const position = getOptionPosition(select);
        if (!position) return true;
        return variant[`option${position}`] === select.value;
      })
    );
  }

  function updateSelectAvailability(variants, selects) {
    selects.forEach((select) => {
      const position = getOptionPosition(select);
      if (!position) return;

      Array.from(select.options).forEach((option) => {
        const allowed = variants.some((variant) => {
          if (variant[`option${position}`] !== option.value) return false;

          return selects.every((otherSelect) => {
            const otherPosition = getOptionPosition(otherSelect);
            if (!otherPosition || otherSelect === select) return true;
            return variant[`option${otherPosition}`] === otherSelect.value;
          });
        });

        option.disabled = !allowed;
      });

      const selectedOption = select.selectedOptions && select.selectedOptions.length ? select.selectedOptions[0] : null;
      if (selectedOption && selectedOption.disabled) {
        const firstEnabled = Array.from(select.options).find((opt) => !opt.disabled);
        if (firstEnabled) select.value = firstEnabled.value;
      }
    });
  }

  function bindLiveControl(input, handler) {
    if (!input || !handler) return;
    input.addEventListener('input', handler);
    input.addEventListener('change', handler);
  }

  function updateSliderPreview(item, slider) {
    if (!item || !slider) return;
    const customInput = item.querySelector('.custom-input');
    const thumb = item.querySelector('.type-archive-slider__thumb');
    if (customInput) {
      fitPreviewToContainer(customInput, slider);
    }
    if (thumb) {
      const min = Number(slider.min || 0);
      const max = Number(slider.max || 100);
      const value = Number(slider.value || 0);
      const ratio = max > min ? ((value - min) / (max - min)) * 100 : 0;
      thumb.style.left = `${ratio}%`;
    }
  }

  function updateBuyLink(item, selectedFont) {
    const buyLink = item.querySelector('.type-archive-action--buy[href]');
    if (!buyLink || buyLink.getAttribute('aria-disabled') === 'true' || !selectedFont) return;

    const destination = new URL(buyLink.href, window.location.origin);
    destination.searchParams.set('font', selectedFont);
    buyLink.href = destination.toString();
  }

  function updateFontPreview(item, selectedFont) {
    if (!item || !selectedFont) return;
    const customInput = item.querySelector('.custom-input');
    const fontSelect = item.querySelector('select[name="font-select"]');
    const fontPills = Array.from(item.querySelectorAll('.type-archive-pill[data-font-value]'));
    let activeFeatureKey = normalizeFeatureKey(selectedFont);
    let activeFontUrl = '';

    if (fontSelect) {
      fontSelect.value = selectedFont;
      const selectedOption = fontSelect.selectedOptions && fontSelect.selectedOptions[0];
      if (selectedOption && selectedOption.dataset.featureKey) {
        activeFeatureKey = normalizeFeatureKey(selectedOption.dataset.featureKey);
      }
      if (selectedOption && selectedOption.dataset.fontUrl) {
        activeFontUrl = selectedOption.dataset.fontUrl;
      }
    }

    fontPills.forEach((pill) => {
      const isActive = pill.dataset.fontValue === selectedFont;
      pill.classList.toggle('is-active', isActive);
      if (isActive && pill.dataset.featureKey) {
        activeFeatureKey = normalizeFeatureKey(pill.dataset.featureKey);
      }
      if (isActive && pill.dataset.fontUrl) {
        activeFontUrl = pill.dataset.fontUrl;
      }
    });

    updateBuyLink(item, selectedFont);

    if (!customInput) return;

    customInput.style.setProperty('font-family', `"${selectedFont}", sans-serif`, 'important');
    renderFeatureControls(item, activeFeatureKey);
    applyFeatureSettings(item);
    const fallbackFeatures = getFeaturesForKey(activeFeatureKey);
    updateFeatureDebug(item, `key:${activeFeatureKey || 'none'} fallback:${fallbackFeatures.length}`);

    if (activeFontUrl) {
      loadFeaturesFromFontUrl(activeFontUrl).then((result) => {
        const stillActive = item.querySelector('.type-archive-pill.is-active');
        if (!stillActive || stillActive.dataset.fontValue !== selectedFont) return;
        if (Array.isArray(result.features) && result.features.length > 0) {
          renderFeatureControls(item, activeFeatureKey, result.features);
          updateFeatureDebug(item, `key:${activeFeatureKey || 'none'} auto:${result.features.length}`);
        } else if (fallbackFeatures.length > 0) {
          updateFeatureDebug(item, `key:${activeFeatureKey || 'none'} fallback:${fallbackFeatures.length} auto:0`);
        } else {
          updateFeatureDebug(item, `key:${activeFeatureKey || 'none'} auto:0 source:${result.source || 'unknown'}`);
        }
      });
    } else {
      updateFeatureDebug(item, `key:${activeFeatureKey || 'none'} no-font-url fallback:${fallbackFeatures.length}`);
    }

    const slider = item.querySelector('.font-size-slider');
    if (slider) {
      fitPreviewToContainer(customInput, slider);
    }

    if (document.fonts && document.fonts.load) {
      document.fonts.load(`1em "${selectedFont}"`).finally(() => {
        customInput.style.setProperty('font-family', `"${selectedFont}", sans-serif`, 'important');
        applyFeatureSettings(item);
        const activeSlider = item.querySelector('.font-size-slider');
        if (activeSlider) {
          fitPreviewToContainer(customInput, activeSlider);
        }
      });
    }
  }

  function initFontPreview(item, syncInputs) {
    const slider = item.querySelector('.font-size-slider');
    const customInput = item.querySelector('.custom-input');
    const fontSelect = item.querySelector('select[name="font-select"]');
    const fontPills = Array.from(item.querySelectorAll('.type-archive-pill[data-font-value]'));
    const transformPills = Array.from(item.querySelectorAll('.type-archive-transform-pill[data-transform-value]'));

    if (slider && customInput) {
      if (!slider.dataset.initializedValue) {
        slider.value = window.innerWidth <= 749 ? slider.min : slider.max;
        slider.dataset.initializedValue = 'true';
      }

      const applySize = () => {
        updateSliderPreview(item, slider);
      };
      bindLiveControl(slider, applySize);
      applySize();
    }

    if (fontSelect && customInput) {
      const applyFont = () => {
        const selectedFont = fontSelect.value;
        if (!selectedFont) return;
        updateFontPreview(item, selectedFont);
      };

      fontSelect.addEventListener('change', applyFont);

      fontPills.forEach((pill) => {
        pill.addEventListener('click', (event) => {
          event.preventDefault();
          if (fontSelect.value === pill.dataset.fontValue) return;
          updateFontPreview(item, pill.dataset.fontValue);
        });
      });

      applyFont();
    }

    if (customInput && transformPills.length > 0) {
      updateTextTransform(item, customInput.dataset.defaultTransform || 'none');
    }

    if (customInput && syncInputs.length > 0) {
      customInput.addEventListener('input', () => {
        syncInputs.forEach((input) => {
          if (input !== customInput) input.value = customInput.value;
        });
        if (slider) {
          updateSliderPreview(item, slider);
        }
      });
    }
  }

  function initPopup(item, body, header, marquee) {
    const trigger = item.querySelector('.popup-trigger');
    const wrapper = item.querySelector('.popup-wrapper');
    const close = item.querySelector('.close-popup');

    if (!trigger || !wrapper || !close) return;

    trigger.addEventListener('click', () => {
      wrapper.classList.add('active');
      body.style.overflow = 'hidden';
      if (header) header.style.background = 'white';
      if (marquee) marquee.style.zIndex = '-1';
    });

    close.addEventListener('click', () => {
      wrapper.classList.remove('active');
      body.style.overflow = 'auto';
      if (header) header.style.background = 'transparent';
      if (marquee) marquee.style.zIndex = '0';
    });

    const scrollTriggers = item.querySelectorAll('.scroll-to-btn');
    const scrollTargets = item.querySelectorAll('.block-info');
    scrollTriggers.forEach((btn, index) => {
      btn.addEventListener('click', () => {
        const target = scrollTargets[index];
        if (target) target.scrollIntoView({ behavior: 'smooth' });
      });
    });

    const legacySelector = wrapper.querySelector('.select-license-demo');
    if (legacySelector) legacySelector.style.display = 'none';
  }

  function initVariantState(item, variants) {
    const selects = Array.from(item.querySelectorAll('.js-variant-option'));
    if (selects.length === 0 || variants.length === 0) return;

    const priceDisplay = item.querySelector('.js-price-display');
    const selectedPriceLegacy = item.querySelector('.selected-variation-price');
    const hiddenVariantInputs = item.querySelectorAll('.js-variant-id-input');
    const legacyVariantSelect = item.querySelector('select.product-variants[name="id"]');

    const applyVariant = () => {
      updateSelectAvailability(variants, selects);
      const selectedVariant = matchVariantBySelections(variants, selects);
      if (!selectedVariant) return;

      if (priceDisplay) priceDisplay.textContent = moneyFromCents(selectedVariant.price);
      if (selectedPriceLegacy) selectedPriceLegacy.textContent = moneyFromCents(selectedVariant.price);
      hiddenVariantInputs.forEach((input) => {
        input.value = selectedVariant.id;
      });
      if (legacyVariantSelect) legacyVariantSelect.value = String(selectedVariant.id);
    };

    selects.forEach((select) => {
      select.addEventListener('change', applyVariant);
    });

    applyVariant();
  }

  function init() {
    const root = document.querySelector('.contenedor-types');
    if (!root) return;

    const body = document.body;
    const header = document.querySelector('header');
    const marquee = document.querySelector('.marq-wrapper');
    const products = Array.from(root.querySelectorAll('.latype'));
    const syncInputs = Array.from(root.querySelectorAll('.custom-input'));

    products.forEach((item) => {
      initPopup(item, body, header, marquee);
      initVariantState(item, parseVariants(item));
      initFontPreview(item, syncInputs);
    });

    root.addEventListener('input', (event) => {
      const slider = event.target.closest('.font-size-slider');
      if (!slider) return;
      updateSliderPreview(slider.closest('.latype'), slider);
    });

    root.addEventListener('change', (event) => {
      const select = event.target.closest('select[name="font-select"]');
      if (!select) return;
      updateFontPreview(select.closest('.latype'), select.value);
    });

    root.addEventListener('click', (event) => {
      const pill = event.target.closest('.type-archive-pill[data-font-value]');
      if (!pill) return;
      event.preventDefault();
      updateFontPreview(pill.closest('.latype'), pill.dataset.fontValue);
    });

    root.addEventListener('click', (event) => {
      const pill = event.target.closest('.type-archive-transform-pill[data-transform-value]');
      if (!pill) return;
      event.preventDefault();
      updateTextTransform(pill.closest('.latype'), pill.dataset.transformValue);
    });

    root.addEventListener('change', (event) => {
      const featureInput = event.target.closest('.type-archive-feature__input');
      if (!featureInput) return;
      syncFeatureState(featureInput.closest('.latype'));
    });

    window.addEventListener('resize', () => {
      products.forEach((item) => {
        const slider = item.querySelector('.font-size-slider');
        if (slider) updateSliderPreview(item, slider);
      });
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
